/**
 * The user's starred collection: a bookmark with target_type 'collection'
 * (/api/user/bookmarks), so it follows the account, not the browser. One star
 * at a time — starring another collection moves it. Home recommends from the
 * starred collection first.
 *
 * The sidebar and Home both show the star, so the state lives in one small
 * module store and every `useStarredCollection()` sees the same value.
 * A server that predates collection bookmarks answers 400; the store then
 * reports `supported: false` and the controls say so instead of failing.
 */
import { useEffect, useState } from 'react';
import { apiUrl } from '../../lib/apiBase';

const headers = (json) => {
  const t = localStorage.getItem('access_token');
  return { ...(t ? { Authorization: `Bearer ${t}` } : {}), ...(json ? { 'Content-Type': 'application/json' } : {}) };
};

// id: number | null (none starred) | undefined (not loaded yet)
let state = { id: undefined, bookmarkIds: [], supported: true, busy: false };
let loading = null;
let loadedFor = null; // the token the state belongs to: a new sign-in reloads it
const listeners = new Set();
const set = (patch) => { state = { ...state, ...patch }; listeners.forEach((l) => l(state)); };

const token = () => localStorage.getItem('access_token');

function load() {
  if (loading && loadedFor === token()) return loading;
  loadedFor = token();
  loading = fetch(apiUrl('/api/user/bookmarks'), { headers: headers() })
    .then((r) => (r.ok ? r.json() : { items: [] }))
    .then((d) => {
      const rows = (d.items || []).filter((b) => b.target_type === 'collection'); // newest first
      set({ id: rows.length ? Number(rows[0].target_id) : null, bookmarkIds: rows.map((b) => b.id) });
    })
    .catch(() => set({ id: null }));
  return loading;
}

async function unstarAll(ids) {
  await Promise.all(ids.map((id) => fetch(apiUrl(`/api/user/bookmarks/${id}`), { method: 'DELETE', headers: headers() })));
}

/** Star `collection` ({id, name}), or clear the star when it is already starred. */
export async function toggleStar(collection) {
  if (!collection || state.busy) return;
  const prev = state;
  const cid = Number(collection.id);
  if (prev.id === cid) {
    set({ id: null, busy: true });
    try {
      await unstarAll(prev.bookmarkIds);
      set({ bookmarkIds: [], busy: false });
    } catch (_) {
      set({ ...prev, busy: false });
    }
    return;
  }
  set({ id: cid, busy: true }); // optimistic
  try {
    const r = await fetch(apiUrl('/api/user/bookmarks'), {
      method: 'POST',
      headers: headers(true),
      body: JSON.stringify({ target_type: 'collection', target_id: String(cid), label: collection.name || null }),
    });
    if (r.status === 400) { set({ ...prev, supported: false, busy: false }); return; }
    if (!r.ok) throw new Error(String(r.status));
    const { id } = await r.json();
    await unstarAll(prev.bookmarkIds.filter((b) => b !== id));
    set({ bookmarkIds: [id], busy: false });
  } catch (_) {
    set({ ...prev, busy: false });
  }
}

export function useStarredCollection() {
  const [s, setS] = useState(state);
  useEffect(() => {
    listeners.add(setS);
    if (state.id === undefined || loadedFor !== token()) load();
    setS(state);
    return () => { listeners.delete(setS); };
  }, []);
  return s;
}

export const STAR_UNSUPPORTED = 'Starring a collection needs a server update that has not been deployed yet.';
